import React, { useState } from 'react';
import useAppToast from '@/hooks/useAppToast';
import { AppButton, AppPopover, AppTag } from '@/components/atoms';
import { useAuth } from '@/shared/providers/AuthContext';
import { RahIconButton } from '../atoms';
import {
  getHqAnnouncements,
  getSystemNotices,
  getTasks,
  type NotificationItem,
} from '../../services/notifications';

/**
 * Topbar utility buttons and their panels.
 *
 * The ConceptDesign keeps three streams apart on purpose — 本部からのお知らせ is
 * pushed by head office, システム通知 is machine-generated, and タスク一覧 is work
 * assigned to me — so they are three buttons, not one bell.
 *
 * `AppPopover` supplies what the prototype hand-rolls for the panels:
 * positioning, outside-click and Esc. The rows are the ConceptDesign's own
 * `.notif-item` grid — antd's `List` is deprecated in v6 and slated for removal,
 * so there is nothing to gain by building on it here. The trigger is
 * `RahIconButton`, not an antd `Button` with a `Badge`: `rah-shell.scss` styles
 * `.top-btn` and its `.ping` dot itself, and antd's own chrome only fought it.
 * ヘルプ sits last with no panel.
 */

/** Outline icon; the emoji ☑ collapses at 15px (ConceptDesign note). */
const TaskIcon: React.FC = () => (
  <svg className="ico-task" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M5.6 2.6H4.3a1.4 1.4 0 0 0-1.4 1.4v9.1a1.4 1.4 0 0 0 1.4 1.4h7.4a1.4 1.4 0 0 0 1.4-1.4V4a1.4 1.4 0 0 0-1.4-1.4h-1.3" />
    <rect x="5.6" y="1.3" width="4.8" height="2.6" rx="0.8" />
    <path d="M5.7 9.1 7.2 10.6 10.5 7.1" />
  </svg>
);

type PanelId = 'hq' | 'system' | 'task';

interface StreamDef {
  id: PanelId;
  title: React.ReactNode;
  trigger: React.ReactNode;
  tooltip: string;
  items: NotificationItem[];
  action: string;
  actionMessage: string;
  /** Show the unread dot on the trigger. */
  unread: boolean;
}

const NotificationList: React.FC<{ items: NotificationItem[] }> = ({ items }) => (
  <div className="notif-list">
    {items.map(item => (
      <div className="notif-item" key={item.id}>
        <span className="notif-ico">{item.icon}</span>
        <span className="notif-title">{item.title}</span>
        {item.priority ? (
          <AppTag color="error" variant="filled">
            {item.time}
          </AppTag>
        ) : (
          <span className="notif-time">{item.time}</span>
        )}
        {item.meta && <span className="notif-meta">{item.meta}</span>}
      </div>
    ))}
  </div>
);

export const RahTopbarActions: React.FC = () => {
  const toast = useAppToast();
  const { hideTenantLogoutButton, logout } = useAuth();
  const [openPanel, setOpenPanel] = useState<PanelId | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const hq = getHqAnnouncements();
  const system = getSystemNotices();
  const tasks = getTasks();

  const streams: StreamDef[] = [
    {
      id: 'hq',
      title: `📢 本部からのお知らせ (${hq.length})`,
      trigger: <span>📢</span>,
      tooltip: '本部からのお知らせ',
      items: hq,
      action: '既読にする',
      actionMessage: '全て既読にしました。',
      unread: hq.length > 0,
    },
    {
      id: 'system',
      title: `💻 システム通知 (${system.length})`,
      trigger: <span>💻</span>,
      tooltip: 'システム通知',
      items: system,
      action: '既読にする',
      actionMessage: '全て既読にしました。',
      unread: false,
    },
    {
      id: 'task',
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <TaskIcon />
          タスク一覧 ({tasks.length})
        </span>
      ),
      trigger: <TaskIcon />,
      tooltip: 'タスク一覧',
      items: tasks,
      action: '完了にする',
      actionMessage: '表示中のタスクを完了にしました。',
      unread: tasks.length > 0,
    },
  ];

  const renderStream = (stream: StreamDef) => (
    <AppPopover
      key={stream.id}
      open={openPanel === stream.id}
      onOpenChange={next => setOpenPanel(next ? stream.id : null)}
      trigger="click"
      placement="bottomRight"
      classNames={{ root: 'rah-screen' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{stream.title}</span>
          <AppButton
            size="small"
            onClick={() => {
              toast.success(stream.actionMessage);
              setOpenPanel(null);
            }}
          >
            {stream.action}
          </AppButton>
        </div>
      }
      content={<NotificationList items={stream.items} />}
    >
      <RahIconButton
        icon={stream.trigger}
        label={stream.tooltip}
        unread={stream.unread}
        expanded={openPanel === stream.id}
      />
    </AppPopover>
  );

  const logoutCurrentSession = async (): Promise<void> => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch {
      toast.error('ログアウトに失敗しました。もう一度お試しください。');
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {renderStream(streams[0])}
      {renderStream(streams[1])}

      <div className="tb-divider" />

      {renderStream(streams[2])}

      <RahIconButton icon="?" label="ヘルプ" onClick={() => toast.info('ヘルプは準備中です。')} />

      {!hideTenantLogoutButton && (
        <>
          <div className="tb-divider" />
          <RahIconButton
            icon={<span aria-hidden>↪</span>}
            label={isLoggingOut ? 'ログアウト中' : 'ログアウト'}
            testId="hitowa-logout-button"
            disabled={isLoggingOut}
            onClick={() => void logoutCurrentSession()}
          />
        </>
      )}
    </>
  );
};

export default RahTopbarActions;
